import { Moon, Sun, Search, User, LogOut, Settings, Menu, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import NotificationBell from '@/components/notifications/NotificationBell';
import OnlineUsers from '@/components/collaboration/OnlineUsers';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { PresenceUser } from '@/hooks/usePresence';
import { useConnectedSources } from '@/hooks/useConnectedSources';
import { useLocalization } from '@/hooks/useLocalization';

interface HeaderProps {
  isDark: boolean;
  onThemeToggle: () => void;
  onMenuClick?: () => void;
  onlineUsers?: PresenceUser[];
  onNavigate?: (view: string) => void;
  onOpenPalette?: () => void;
}

const Header = ({ isDark, onThemeToggle, onMenuClick, onlineUsers = [], onNavigate, onOpenPalette }: HeaderProps) => {
  const { data: sources = [] } = useConnectedSources();
  const connected = sources.filter((x) => x.status !== 'off').length;
  const { user, signOut } = useAuth();
  const { tenantSlug, tenantName } = useTenant();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useLocalization();

  const tenantPath = (suffix: string) =>
    tenantSlug ? `/${tenantSlug}${suffix}` : suffix;

  return (
    <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 md:px-6">
      {/* Mobile Menu Button */}
      {isMobile && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          aria-label={t('Open navigation menu')}
          className="text-muted-foreground hover:text-foreground me-2"
        >
          <Menu className="w-5 h-5" />
        </Button>
      )}

      {/* Search / command palette trigger (Cmd/Ctrl+K) */}
      <div className="flex items-center gap-4 flex-1 max-w-xl">
        <button
          type="button"
          onClick={onOpenPalette}
          aria-label={t('Search pages (Cmd or Ctrl + K)')}
          className="relative flex h-10 w-full items-center rounded-md border border-input bg-background ps-10 pe-3 text-start text-sm text-muted-foreground hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Search className="absolute top-1/2 -translate-y-1/2 w-4 h-4 start-3" aria-hidden="true" />
          <span className="flex-1 truncate">{t('Go to…')}</span>
          <kbd className="hidden sm:inline rounded border border-border px-1.5 font-mono text-[11px]">⌘K</kbd>
        </button>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 md:gap-2">
        {/* Org name chip */}
        {tenantName && (
          <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted/60 border border-border">
            <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground max-w-[120px] truncate">{tenantName}</span>
          </div>
        )}

        {/* Real connection status (from integration_configs), never a static badge */}
        <button
          type="button"
          onClick={() => navigate(tenantPath('/integrations'))}
          className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary border border-border hover:bg-accent transition-colors"
        >
          <span className={`h-2 w-2 rounded-full ${connected > 0 ? 'bg-success' : 'bg-muted-foreground/50'}`} aria-hidden="true" />
          <span className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">
            {connected > 0 ? `${t('Connected')} · ${connected}` : t('Connect sources')}
          </span>
        </button>

        {/* Online Users */}
        {onlineUsers.length > 0 && (
          <div className="hidden md:block">
            <OnlineUsers users={onlineUsers} maxVisible={3} />
          </div>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={onThemeToggle}
          aria-label={t(isDark ? 'Switch to light theme' : 'Switch to dark theme')}
          className="text-muted-foreground hover:text-foreground"
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>

        <NotificationBell onNavigate={onNavigate} />

        <div className="w-px h-8 bg-border mx-1 md:mx-2 hidden sm:block" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 text-muted-foreground hover:text-foreground px-2 md:px-3">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <User className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="text-sm font-medium max-w-[80px] md:max-w-[120px] truncate hidden sm:inline">
                {user?.email?.split('@')[0] || t('User')}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-popover">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.email?.split('@')[0]}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate(tenantPath('/settings'))} className="cursor-pointer">
              <Settings className="w-4 h-4 me-2" />
              
              {t('Settings')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive cursor-pointer">
              <LogOut className="w-4 h-4 me-2" />
              
              {t('Sign out')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
